import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";

import { Icon, IconButton, StatePanel } from "../components/ui";
import ChannelList from "./ChannelList";
import MessageThread from "./MessageThread";
import { useChatChannels } from "./useMerchantChat";
import { useQuickChat } from "./quickChatStore";
import "./rc-merchant-chat.scss";
import "./rc-quick-chat.scss";

/**
 * Quick chat — the docked side panel, so answering Retail Circle never means
 * leaving the page you are on. Same conversations as /messages (identical
 * react-query keys, so unread counts and new messages agree on both).
 *
 * Two shells over one body:
 *  - docked: a 380px panel pinned to the right edge under the topbar;
 *  - popped out: a real always-on-top OS window via the Document
 *    Picture-in-Picture API, for keeping the chat visible while working in
 *    another tab. Chromium-only — the button hides itself where the API is
 *    absent (Safari/Firefox), which is why the dock is the primary surface
 *    and the pop-out is a bonus.
 */

const PIP_SUPPORTED = typeof window !== "undefined" && "documentPictureInPicture" in window;

/** Clone the app's styles into the PiP document — it starts blank. */
const adoptStyles = (target) => {
  document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
    target.head.appendChild(node.cloneNode(true));
  });
  // Carry the theme across so the popped-out window is not light-on-dark.
  const theme = document.documentElement.getAttribute("data-theme");
  if (theme) target.documentElement.setAttribute("data-theme", theme);
  target.body.classList.add("rc-quick-chat-pip");
};

const QuickChatBody = ({ channels, activeChannel, isLoading, isError, onSelect, onBack }) => {
  if (isLoading) return <StatePanel state="loading" />;

  if (isError) {
    return <StatePanel state="error" description="Couldn't load your conversations." />;
  }

  if (!channels.length) {
    return (
      <StatePanel
        state="empty"
        icon="MessageCircle"
        title="No conversations yet"
        description="Your channel with the Retail Circle team appears here once your store is set up."
      />
    );
  }

  if (!activeChannel) {
    return (
      <div className="rc-quick-chat__list">
        <ChannelList channels={channels} activeId={null} onSelect={onSelect} />
      </div>
    );
  }

  return (
    <div className="rc-quick-chat__thread">
      <button type="button" className="rc-quick-chat__back" onClick={onBack}>
        <Icon name="ArrowLeft" size="xs" /> All conversations
      </button>
      <MessageThread channel={activeChannel} />
    </div>
  );
};

const QuickChatDock = () => {
  const history = useHistory();
  const { isOpen, channelId, poppedOut, close, select, clearSelection, setPoppedOut } = useQuickChat();
  const [pipContainer, setPipContainer] = useState(null);
  const pipWindowRef = useRef(null);

  // Channels load only while the dock is actually in use.
  const { data: channels = [], isLoading, isError } = useChatChannels(isOpen || poppedOut);

  const activeChannel = useMemo(
    () => channels.find((c) => c.id === channelId) || null,
    [channels, channelId]
  );

  // Close on Escape, but never while the user is inside the pop-out window.
  useEffect(() => {
    if (!isOpen || poppedOut) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, poppedOut, close]);

  const popOut = async () => {
    if (!PIP_SUPPORTED) return;
    try {
      const pipWindow = await window.documentPictureInPicture.requestWindow({
        width: 420,
        height: 620,
      });
      pipWindowRef.current = pipWindow;
      adoptStyles(pipWindow.document);

      const mount = pipWindow.document.createElement("div");
      mount.className = "rc-quick-chat rc-quick-chat--pip";
      pipWindow.document.body.appendChild(mount);
      setPipContainer(mount);
      setPoppedOut(true);

      pipWindow.addEventListener("pagehide", () => {
        setPipContainer(null);
        pipWindowRef.current = null;
        setPoppedOut(false);
      });
    } catch {
      // Blocked (no user gesture / unsupported) — stay docked.
    }
  };

  const header = (inPip) => (
    <header className="rc-quick-chat__head">
      <span className="rc-quick-chat__title">
        <Icon name="MessageCircle" size="sm" />
        {activeChannel ? activeChannel.name : "Messages"}
      </span>
      <span className="rc-quick-chat__actions">
        {!inPip && PIP_SUPPORTED && (
          <IconButton variant="ghost" size="sm" label="Pop out into its own window" onClick={popOut}>
            <Icon name="ExternalLink" size="xs" />
          </IconButton>
        )}
        <IconButton
          variant="ghost"
          size="sm"
          label="Open the full Messages page"
          onClick={() => {
            close();
            history.push(activeChannel ? `/messages/${activeChannel.id}` : "/messages");
          }}
        >
          <Icon name="LayoutGrid" size="xs" />
        </IconButton>
        {!inPip && (
          <IconButton variant="ghost" size="sm" label="Close quick chat" onClick={close}>
            <Icon name="X" size="xs" />
          </IconButton>
        )}
      </span>
    </header>
  );

  const body = (
    <QuickChatBody
      channels={channels}
      activeChannel={activeChannel}
      isLoading={isLoading}
      isError={isError}
      onSelect={(channel) => select(channel.id)}
      onBack={clearSelection}
    />
  );

  // Popped out: the dock stays closed and the panel lives in the PiP window.
  if (pipContainer) {
    return createPortal(
      <>
        {header(true)}
        {body}
      </>,
      pipContainer
    );
  }

  if (!isOpen) return null;

  return (
    <aside
      className="rc-quick-chat"
      role="dialog"
      aria-label="Quick chat with Retail Circle"
    >
      {header(false)}
      {body}
    </aside>
  );
};

export default QuickChatDock;
